import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Activity, Loader2, RefreshCw, CheckCircle2, XCircle, Copy, KeyRound, Settings2 } from "lucide-react";
import ClickOpportunities from "./ClickOpportunities";
import RewriteImpact from "./RewriteImpact";

const ADMIN_ROLES = ["Super Admin", "Admin", "Pollen Dots User"];
const GROUPS = [
  { key: "blogs", label: "Blogs" },
  { key: "tours", label: "Tour pages" },
  { key: "other", label: "Other pages" },
];
const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());

function ConnectCard({ settings, onDone }) {
  const [json, setJson] = useState("");
  const [pid, setPid] = useState(settings?.ga4_property_id || "456648066");
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState(null);

  const connect = async () => {
    setBusy(true);
    try {
      await api.post("/admin/traffic/settings", { ...(json.trim() ? { service_account_json: json } : {}), ga4_property_id: pid });
      const r = await api.post("/admin/traffic/test");
      setTest(r.data);
      if (r.data.ga4?.ok && r.data.gsc?.ok) {
        toast.success("Connected! Running the first sync…");
        await api.post("/admin/traffic/sync", { days: 28 });
        onDone();
      } else {
        toast.error("Connected partially — see the checks below");
        onDone(true);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Connection failed"); }
    setBusy(false);
  };

  const email = settings?.client_email;

  return (
    <div className="rounded-xl border border-forest/10 bg-beige p-5 max-w-3xl" data-testid="traffic-connect">
      <div className="text-[10px] uppercase tracking-widest text-clay flex items-center gap-1.5 mb-2"><KeyRound className="w-3.5 h-3.5" /> Connect Google — one time, free forever</div>
      <ol className="text-sm text-ink space-y-1.5 list-decimal ml-5 mb-4 leading-relaxed">
        <li>At <span className="font-medium">console.cloud.google.com</span>: create a project → enable <b>Google Analytics Data API</b> + <b>Google Search Console API</b> (APIs &amp; Services → Library)</li>
        <li>IAM &amp; Admin → Service Accounts → Create → open it → Keys → Add key → <b>Create new key → JSON</b>. Your browser downloads a small .json file — open it in any text editor and paste it below</li>
        <li>Copy the service account's email and add it in <b>GA4</b> (Admin → Property access management → Viewer) and in <b>Search Console</b> (Settings → Users and permissions → Full)</li>
      </ol>
      {email && (
        <div className="mb-3 flex items-center gap-2 text-xs bg-cream border border-forest/15 rounded-lg px-3 py-2">
          <span className="text-inkmute">Service account:</span>
          <span className="text-ink font-medium break-all">{email}</span>
          <button onClick={() => { navigator.clipboard.writeText(email); toast.success("Copied"); }} className="text-forest shrink-0"><Copy className="w-3.5 h-3.5" /></button>
        </div>
      )}
      <textarea
        data-testid="traffic-sa-json"
        value={json} onChange={(e) => setJson(e.target.value)}
        placeholder={email ? "Key already saved — paste here only to replace it" : '{ "type": "service_account", "project_id": ... }  ← paste the whole file'}
        rows={5}
        className="w-full bg-cream border border-forest/15 rounded-lg px-3 py-2 text-xs font-mono text-ink mb-3"
      />
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-xs text-inkmute">
          GA4 property ID
          <input data-testid="traffic-ga4-id" value={pid} onChange={(e) => setPid(e.target.value)}
            className="w-32 bg-cream border border-forest/15 rounded-lg px-3 py-2 text-sm text-ink" />
        </label>
        <button data-testid="traffic-connect-btn" onClick={connect} disabled={busy || (!json.trim() && !email)}
          className="inline-flex items-center gap-2 bg-forest text-cream px-5 py-2 rounded-full text-xs uppercase tracking-widest hover:bg-forestDeep disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
          {busy ? "Connecting…" : "Connect & test"}
        </button>
        <span className="text-[11px] text-inkmute">Search Console property is auto-detected. The key never leaves the server.</span>
      </div>
      {test && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[["ga4", "GA4 (visits & engagement)"], ["gsc", "Search Console (impressions & clicks)"]].map(([k, label]) => (
            <div key={k} className={`rounded-lg border p-3 flex items-start gap-2 ${test[k]?.ok ? "border-forest/15 bg-cream" : "border-red-300 bg-red-50"}`}>
              {test[k]?.ok ? <CheckCircle2 className="w-4 h-4 text-forest mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />}
              <div className="text-xs">
                <div className="font-medium text-ink">{label}</div>
                <div className="text-inkmute mt-0.5">{test[k]?.ok ? (k === "gsc" ? `Connected: ${test[k].site_url}` : "Connected") : test[k]?.error}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TrafficTab() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const [settings, setSettings] = useState(null);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(28);
  const [group, setGroup] = useState("blogs");
  const [syncing, setSyncing] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [impactKey, setImpactKey] = useState(0);

  const load = async (d = days) => {
    const [s, p] = await Promise.all([
      api.get("/admin/traffic/settings"),
      api.get(`/admin/traffic/pages?days=${d}`),
    ]);
    setSettings(s.data);
    setData(p.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/traffic/sync", { days });
      if (r.data.errors?.length) toast.error(r.data.errors[0]);
      else toast.success(`Synced ${r.data.gsc_rows + r.data.ga4_rows} rows`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    setSyncing(false);
  };

  const changeDays = async (d) => { setDays(d); await load(d).catch(() => {}); };

  if (!settings) return <div className="text-inkmute text-sm">Loading…</div>;

  const connected = settings.configured && settings.ga4_status?.ok !== false;
  const hasData = data && data.totals.pages > 0;

  return (
    <div data-testid="traffic-tab">
      {(!settings.configured || showConnect) && isAdmin && (
        <div className="mb-6"><ConnectCard settings={settings} onDone={() => { setShowConnect(false); load().catch(() => {}); }} /></div>
      )}
      {!settings.configured && !isAdmin && (
        <div className="text-sm text-inkmute mb-6">Traffic tracking isn't connected yet — ask an admin to attach the Google key.</div>
      )}

      {settings.configured && (
        <div className="flex flex-wrap items-center gap-3 mb-6 text-xs text-inkmute">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ${connected ? "bg-forest text-cream" : "bg-pollen text-ink"}`}>
            <Activity className="w-3.5 h-3.5" /> {connected ? "Tracking connected" : "Connected — check access"}
          </span>
          {settings.gsc_site_url && <span>GSC: {settings.gsc_site_url}</span>}
          {settings.ga4_property_id && <span>GA4: {settings.ga4_property_id}</span>}
          {settings.last_sync_at && <span>Last sync {new Date(settings.last_sync_at).toLocaleString()}</span>}
          <span>· auto-syncs daily 04:15 UTC</span>
          {isAdmin && (
            <>
              <button data-testid="traffic-sync-btn" onClick={syncNow} disabled={syncing} className="inline-flex items-center gap-1.5 text-forest link-underline disabled:opacity-50">
                {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Sync now
              </button>
              <button onClick={() => setShowConnect(!showConnect)} className="inline-flex items-center gap-1.5 text-forest link-underline"><Settings2 className="w-3.5 h-3.5" /> Connection</button>
            </>
          )}
        </div>
      )}

      {settings.configured && !hasData && (
        <div className="text-sm text-inkmute">No traffic data yet — {settings.last_sync?.errors?.length ? settings.last_sync.errors.join(" · ") : "run a sync (data appears once Google grants the key access, and Search Console data runs ~2 days behind)."}</div>
      )}

      {hasData && (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {[7, 28, 90].map((d) => (
              <button key={d} onClick={() => changeDays(d)}
                className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full ${days === d ? "bg-forest text-cream" : "border border-forest/20 text-forest hover:bg-beige"}`}>
                {d} days
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[["Search impressions", data.totals.impressions], ["Search clicks", data.totals.clicks], ["Site visits (GA4)", data.totals.pageviews], ["Users (GA4)", data.totals.users]].map(([label, v]) => (
              <div key={label} className="rounded-xl border border-forest/10 bg-beige p-4">
                <div className="text-[10px] uppercase tracking-widest text-clay">{label}</div>
                <div className="font-serif text-3xl text-ink mt-1">{fmt(v)}</div>
              </div>
            ))}
          </div>

          {data.series.length > 1 && (
            <div className="rounded-xl border border-forest/10 bg-beige p-5 mb-6">
              <div className="text-[10px] uppercase tracking-widest text-clay mb-3">Daily clicks (bars) &amp; impressions (line height reference)</div>
              <div className="flex items-end gap-[3px] h-24">
                {data.series.map((d) => {
                  const maxC = Math.max(...data.series.map((x) => x.clicks), 1);
                  return <div key={d.date} className="flex-1 bg-forest rounded-t hover:bg-forestDeep" style={{ height: `${Math.max(3, (d.clicks / maxC) * 100)}%` }} title={`${d.date}: ${d.clicks} clicks · ${d.impressions} impressions · ${d.pageviews} views`} />;
                })}
              </div>
              <div className="flex justify-between text-[10px] text-inkmute mt-1"><span>{data.series[0].date}</span><span>{data.series[data.series.length - 1].date}</span></div>
            </div>
          )}

          <RewriteImpact refreshKey={impactKey} />
          <ClickOpportunities days={days} isAdmin={isAdmin} onAppliedChange={() => setImpactKey((k) => k + 1)} />

          <div className="flex gap-2 mb-3">
            {GROUPS.map((g) => (
              <button key={g.key} data-testid={`traffic-group-${g.key}`} onClick={() => setGroup(g.key)}
                className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full ${group === g.key ? "bg-forest text-cream" : "border border-forest/20 text-forest hover:bg-beige"}`}>
                {g.label} ({(data.groups[g.key] || []).length})
              </button>
            ))}
          </div>
          <div className="rounded-xl border border-forest/10 bg-beige overflow-auto max-h-[560px]" data-testid="traffic-table">
            <table className="w-full text-sm">
              <thead className="bg-cream text-[10px] uppercase tracking-widest text-clay sticky top-0">
                <tr>
                  <th className="text-left p-3">Page</th>
                  <th className="text-right p-3">Impressions</th>
                  <th className="text-right p-3">Clicks</th>
                  <th className="text-right p-3 hidden md:table-cell">CTR</th>
                  <th className="text-right p-3 hidden md:table-cell">Position</th>
                  <th className="text-right p-3">Visits</th>
                  <th className="text-right p-3 hidden lg:table-cell">Users</th>
                </tr>
              </thead>
              <tbody>
                {(data.groups[group] || []).map((p) => (
                  <tr key={p.path} className="border-t border-forest/10">
                    <td className="p-3 text-xs text-ink max-w-[340px] truncate" title={p.path}>{p.path}</td>
                    <td className="p-3 text-right text-xs text-inkmute">{fmt(p.impressions)}</td>
                    <td className="p-3 text-right text-xs font-medium text-ink">{fmt(p.clicks)}</td>
                    <td className="p-3 text-right text-xs text-inkmute hidden md:table-cell">{(p.ctr * 100).toFixed(1)}%</td>
                    <td className="p-3 text-right text-xs text-inkmute hidden md:table-cell">{p.position ? p.position.toFixed(1) : "—"}</td>
                    <td className="p-3 text-right text-xs text-ink">{fmt(p.pageviews)}</td>
                    <td className="p-3 text-right text-xs text-inkmute hidden lg:table-cell">{fmt(p.users)}</td>
                  </tr>
                ))}
                {(data.groups[group] || []).length === 0 && (
                  <tr><td colSpan={7} className="p-4 text-center text-xs text-inkmute">No pages in this group for the selected window.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
